import { adminApiCall } from "@/lib/api";

/**
 * 将上传文件路径转成可访问的完整 URL
 * /uploads/xxx → 通过 /api/files/xxx 走 Node 后端代理（避免 nginx alias 路径不匹配）
 */
export function resolveUploadUrl(url: string): string {
  if (!url) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  // /uploads/xxx → /api/files/xxx
  if (url.startsWith("/uploads/")) {
    const filePath = url.replace("/uploads/", "");
    return `${window.location.origin}/api/files/${filePath}`;
  }
  if (url.startsWith("/api/files/")) {
    return `${window.location.origin}${url}`;
  }
  // bare filename → /api/files/filename
  if (!url.includes("/") && url.length > 0) {
    return `${window.location.origin}/api/files/${url}`;
  }
  return url;
}

export function getPublicUrl(path: string, _bucket?: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  if (path.startsWith("/uploads/")) {
    return resolveUploadUrl(path);
  }
  return resolveUploadUrl(`/uploads/${path}`);
}

export function extractPathFromUrl(url: string, _bucket?: string): string | null {
  if (!url) return null;

  // Local uploads URL - handle both /uploads/ and /api/files/ paths
  const localMatch = url.match(/\/(?:uploads|api\/files)\/(.+)$/);
  if (localMatch) {
    return localMatch[1];
  }

  // Legacy Supabase URLs
  const publicMatch = url.match(/\/storage\/v1\/object\/public\/[^/]+\/(.+?)(\?|$)/);
  if (publicMatch) {
    return decodeURIComponent(publicMatch[1]);
  }

  const signedMatch = url.match(/\/storage\/v1\/object\/sign\/[^/]+\/(.+?)(\?|$)/);
  if (signedMatch) {
    return decodeURIComponent(signedMatch[1]);
  }

  return null;
}

export async function deleteStorageFile(path: string, bucket: string): Promise<void> {
  // Local deployment: file deletion is handled by the backend
  // For now, we just log - actual deletion requires backend API support
  console.log("Delete file requested:", path, bucket);
}

export async function deleteFileByUrl(url: string, bucket: string): Promise<void> {
  const path = extractPathFromUrl(url, bucket);
  if (path) {
    await deleteStorageFile(path, bucket);
  }
}

export function getFileNameFromUrl(url: string): string {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const parts = pathname.split("/");
    const last = parts[parts.length - 1];
    return decodeURIComponent(last).replace(/^\d+_[a-z0-9]+_/, "");
  } catch {
    return "download";
  }
}

export async function downloadFile(url: string, fileName?: string): Promise<void> {
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || getFileNameFromUrl(url);
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export async function downloadBlob(url: string, fileName?: string): Promise<void> {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName || getFileNameFromUrl(url);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch (e) {
    console.error("Download blob failed:", e);
    downloadFile(url, fileName);
  }
}